import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ShieldCheck, X } from "lucide-react";
import AuthLayout from "@/components/AuthLayout";
import { api, formatApiError } from "@/lib/api";

const CERTS = [
  "I confirm that I am at least 18 years old and legally eligible to use Monroe Society.",
  "I confirm that I am creating and operating this account voluntarily and that the information I provide is truthful and accurate.",
  "I agree not to use Monroe Society to advertise, promote, arrange, or facilitate unlawful activity, trafficking, exploitation, or coercion.",
  "I confirm that I own or have permission to use all photographs and content I submit.",
  "I agree to Monroe Society's Terms of Service, Advertising Policy, Code of Conduct, Privacy Policy, and Safety Policy.",
];

const POLICIES = [
  "You must be 18 years of age or older and legally eligible to use the platform.",
  "You are responsible for complying with all applicable federal, state, and local laws.",
  "Monroe Society is an advertising platform only and does not authorize or permit unlawful activity.",
  "Your account must be created and maintained voluntarily, without force, coercion, trafficking, or exploitation.",
  "Your account must remain under your personal control and may not be sold, transferred, or operated by another person.",
  "You must successfully complete our age and identity verification before your profile can be published.",
  "All information, photographs, and content you submit must be truthful, accurate, authorized, and lawful.",
  "Minors, trafficking, exploitation, coercion, nonconsensual content, and other unlawful content are strictly prohibited.",
];

const inputCls = "w-full bg-elevated border hairline-gold px-4 py-3.5 text-sm text-neutral-200 placeholder:text-neutral-600 focus:outline-none focus:border-amber-500/50 transition-colors";

export default function ProviderJoin() {
  const [form, setForm] = useState({ display_name: "", email: "", phone: "", password: "", confirm: "" });
  const [certs, setCerts] = useState(CERTS.map(() => false));
  const [verified, setVerified] = useState(false);
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  const allCerts = certs.every(Boolean);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password !== form.confirm) return setError("Passwords do not match.");
    if (!verified) return setError("Please complete age & identity verification before creating your account.");
    if (!allCerts) return setError("All required certifications must be confirmed.");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", {
        email: form.email,
        password: form.password,
        display_name: form.display_name,
        phone: form.phone,
        role: "provider",
        certifications: CERTS,
        verification_method: "third_party_simulated",
      });
      setDone(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <AuthLayout eyebrow="Provider Registration" title={<>Welcome to the <span className="italic gold-text">Society</span></>}>
        <div data-testid="provider-success-panel" className="glass-panel p-8 sm:p-10 text-center">
          <ShieldCheck className="mx-auto text-gold mb-5" size={36} strokeWidth={1.25} />
          <h2 className="font-serif text-2xl text-amber-100/95 mb-3">Account created, {done.display_name}</h2>
          <p className="text-sm text-neutral-400 font-light leading-relaxed mb-2">
            Your account status is <span className="text-gold uppercase tracking-[0.15em] text-xs">Pending Verification</span>.
          </p>
          <p className="text-sm text-neutral-400 font-light leading-relaxed">
            Our review team will confirm your verification results and registration requirements before your profile
            can be published to the directory.
          </p>
          <Link to="/" data-testid="provider-success-home-link" className="inline-block mt-8 px-8 py-3 border hairline-gold text-xs uppercase tracking-[0.2em] text-amber-100/90 hover:border-amber-400/60 transition-colors">
            Return Home
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      eyebrow="Provider Registration"
      title={<>Create your <span className="italic gold-text">provider account</span></>}
      subtitle="Monroe Society is a U.S.-based advertising platform for independent adult service providers. Before creating an account, please review and agree to the following."
    >
      <ul className="space-y-2.5 mb-12 p-6 bg-onyx border hairline-gold">
        {POLICIES.map((p) => (
          <li key={p} className="flex gap-3 text-sm text-neutral-400 font-light leading-relaxed">
            <span className="w-1 h-1 rotate-45 bg-gold/60 mt-2 shrink-0" />
            {p}
          </li>
        ))}
      </ul>

      <form data-testid="provider-signup-form" onSubmit={submit} className="space-y-10">
        <div className="space-y-5">
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-neutral-400 mb-2">Working Name / Display Name*</label>
            <input data-testid="provider-display-name-input" required value={form.display_name} onChange={set("display_name")} placeholder="Enter your display name" className={inputCls} />
            <p className="mt-1.5 text-xs text-neutral-600">Your display name does not have to be your legal name.</p>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-neutral-400 mb-2">Email Address*</label>
            <input data-testid="provider-email-input" type="email" required value={form.email} onChange={set("email")} placeholder="Enter email" className={inputCls} />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-neutral-400 mb-2">Phone Number*</label>
            <input data-testid="provider-phone-input" type="tel" required value={form.phone} onChange={set("phone")} placeholder="Enter phone" className={inputCls} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-neutral-400 mb-2">Password*</label>
              <input data-testid="provider-password-input" type="password" required minLength={8} value={form.password} onChange={set("password")} placeholder="Create password" className={inputCls} />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-neutral-400 mb-2">Confirm Password*</label>
              <input data-testid="provider-confirm-password-input" type="password" required value={form.confirm} onChange={set("confirm")} placeholder="Confirm password" className={inputCls} />
            </div>
          </div>
        </div>

        <div className="p-6 sm:p-8 bg-onyx border hairline-gold">
          <h3 className="font-serif text-2xl text-amber-100/95 mb-4">Age & Identity Verification</h3>
          <p className="text-sm text-neutral-400 font-light leading-relaxed mb-4">
            All providers must complete our required third-party age and identity verification before their profile
            can be published. You may be asked to provide a valid government-issued photo ID, date of birth, and a
            selfie or liveness check. Your verification information is processed through our designated verification
            provider; Monroe Society receives only the verification result and information reasonably necessary to
            administer your account.
          </p>
          <button
            type="button"
            data-testid="provider-start-verification-button"
            onClick={() => setVerifyOpen(true)}
            className={`px-8 py-3 text-xs uppercase tracking-[0.2em] font-medium transition-all duration-300 ${
              verified ? "border border-emerald-700 text-emerald-400" : "bg-gold text-obsidian hover:bg-gold-bright"
            }`}
          >
            {verified ? "Verification Complete" : "Start Verification"}
          </button>
        </div>

        <div className="p-6 sm:p-8 bg-onyx border hairline-gold">
          <h3 className="font-serif text-2xl text-amber-100/95 mb-6">Required Certifications</h3>
          <div className="space-y-4">
            {CERTS.map((c, i) => (
              <label key={c} className="flex items-start gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  data-testid={`provider-cert-checkbox-${i + 1}`}
                  checked={certs[i]}
                  onChange={() => setCerts(certs.map((v, j) => (j === i ? !v : v)))}
                  className="mt-1 h-4 w-4 shrink-0 appearance-none border hairline-gold bg-elevated checked:bg-gold checked:border-gold transition-colors cursor-pointer"
                />
                <span className="text-sm text-neutral-400 font-light leading-relaxed group-hover:text-neutral-300 transition-colors">{c}</span>
              </label>
            ))}
          </div>
        </div>

        <p className="text-xs text-neutral-500 leading-relaxed">
          Account status: your account will remain <span className="text-gold/80">Pending Verification</span> until
          all required verification and registration requirements are completed.
        </p>

        {error && <p data-testid="provider-form-error" className="text-sm text-red-400">{error}</p>}

        <button
          type="submit"
          data-testid="provider-form-submit-button"
          disabled={loading}
          className="w-full py-4 bg-gold text-obsidian text-sm uppercase tracking-[0.25em] font-medium hover:bg-gold-bright hover:shadow-[0_0_30px_rgba(212,175,55,0.3)] transition-all duration-300 disabled:opacity-50"
        >
          {loading ? "Creating Account…" : "Create Provider Account"}
        </button>

        <p className="text-sm text-neutral-500 text-center">
          Already have an account?{" "}
          <Link to="/signin" data-testid="provider-signin-link" className="text-gold hover:text-gold-bright transition-colors">Sign In</Link>
        </p>
      </form>

      <AnimatePresence>
        {verifyOpen && (
          <motion.div
            data-testid="provider-verification-modal"
            className="fixed inset-0 z-[90] flex items-center justify-center bg-obsidian/90 backdrop-blur-xl px-4"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setVerifyOpen(false)}
          >
            <motion.div
              className="glass-panel max-w-md w-full p-8"
              initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between mb-5">
                <ShieldCheck className="text-gold" size={30} strokeWidth={1.25} />
                <button data-testid="provider-verification-close" onClick={() => setVerifyOpen(false)} className="text-neutral-500 hover:text-gold transition-colors" aria-label="Close">
                  <X size={18} />
                </button>
              </div>
              <h3 className="font-serif text-2xl text-amber-100/95 mb-3">Third-Party Verification</h3>
              <p className="text-sm text-neutral-400 font-light leading-relaxed mb-6">
                In production, this step connects to our designated verification partner (Yoti) for government ID,
                date-of-birth, and liveness checks. This preview simulates a successful verification result.
              </p>
              <button
                data-testid="provider-simulate-verification-button"
                onClick={() => { setVerified(true); setVerifyOpen(false); }}
                className="w-full py-3.5 bg-gold text-obsidian text-xs uppercase tracking-[0.2em] font-medium hover:bg-gold-bright transition-colors"
              >
                Simulate Successful Verification
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </AuthLayout>
  );
}
